import React, { useRef, useState } from 'react';
import styled from 'styled-components';
import { forIE } from '@westwing/ui-kit/breakPoints';
import LooksTileUiKit from '@westwing/ui-kit/LooksTile';
import TextBox from '@westwing/ui-kit/TextBox';
import { PageType, urlStateToWebUrl, webUrlToUrlState } from '@westwing/looks-url-service';
import { useDispatch } from 'react-redux';
import { useLooksTracking } from '../hooks/useLooksTracking';
import { WishlistBadge } from '../WishlistBadge';
import LLPImage from './LLPImage';
import HybridLink from 'Client/components/Generic/Links/HybridLink';
import useBellaTranslator from 'Client/hooks/useTranslator';
import { LookListing } from 'Client/redux/looksPage/types';
import { textLinesLimitClipCss } from 'Client/components/Generic/Text/StyledText';
import useBellaPush from 'Client/hooks/useBellaHistoryPush';
import useRouter from 'Client/hooks/useRouter';
import useElementIntersection from 'Client/hooks/useElementIntersection';
import { setLDPKnownInfoAction } from 'Client/redux/lookDetail/actions';
import { TrackLooksActions } from 'Common/ga/types';
import useFeatureToggle from 'Client/hooks/useFeatureToggle';

const StyledLooksTileUiKit = styled(LooksTileUiKit)`
    ${forIE`
        flex-basis: calc( (100% - 20px) / 4 );
    `}
`;

export const Title = styled(TextBox.Header)`
    ${textLinesLimitClipCss(1)}
    ${forIE`
        display: block;
    `}
`;

type LooksTileProps = {
    addDownWardRef?: boolean;
    index: number;
    look: LookListing;
    nextPageDownward: number;
    className?: string;
};

export enum LooksTileTrackingIdentifier {
    LOOK = 'look',
    CATEGORY = 'category',
}

export const getLooksTileTrackingStrategy = (
    identifier: LooksTileTrackingIdentifier = LooksTileTrackingIdentifier.LOOK,
    look: LookListing
) => {
    const defaultTrackingStrategy = { action: TrackLooksActions.LLPLook, label: look.trackingName };

    let trackingStrategy = defaultTrackingStrategy;

    if (identifier === LooksTileTrackingIdentifier.CATEGORY) {
        trackingStrategy = { action: TrackLooksActions.LLPCategory, label: look.roomTrackingName };
    }

    return trackingStrategy;
};

const LooksTile = ({ index, look, addDownWardRef, nextPageDownward }: LooksTileProps) => {
    const looksLastTileRef = useRef<HTMLDivElement>(null);
    const [showRef, setShowRef] = useState<boolean>(true);
    const t = useBellaTranslator();
    const dispatch = useDispatch();
    const bellaPush = useBellaPush();
    const { p100NewLooksWishlist } = useFeatureToggle();
    const {
        location: { pathname, search },
    } = useRouter();
    const { styles } = webUrlToUrlState(pathname);
    const isPortrait = !(index % 2);

    const { LLP: LLPTracking } = useLooksTracking();

    const handleClick = (identifier?: LooksTileTrackingIdentifier) => {
        dispatch(setLDPKnownInfoAction(look));
        const { action, label } = getLooksTileTrackingStrategy(identifier, look);
        LLPTracking.track(action, label);
    };

    const roomURL = urlStateToWebUrl({ pageType: PageType.LLP, rooms: [look.roomSlug], styles });
    const ldpURL = urlStateToWebUrl({ pageType: PageType.LDP, look: look.slug });

    useElementIntersection(
        looksLastTileRef,
        (pathIdentifier: string) => {
            if (`${pathname}${search}` !== pathIdentifier) {
                // Remove ref once the push is initiated.
                setShowRef(false);
                bellaPush(pathIdentifier, true, undefined, true);
            }
        },
        { rootMargin: '0px', threshold: 0.75 },
        `${pathname}?page=${nextPageDownward}`
    );

    const onWishlistBadgeClick = (newIsSelectedValue: boolean) =>
        LLPTracking.track(
            newIsSelectedValue ? TrackLooksActions.LLPAddLookWishlist : TrackLooksActions.LLPRemoveLookWishlist,
            look.trackingName
        );

    const props = addDownWardRef && showRef ? { forwaredRef: looksLastTileRef } : {};

    return (
        <StyledLooksTileUiKit {...props} data-testid="looks-tile">
            {p100NewLooksWishlist && (
                <LooksTileUiKit.TopRightBadge>
                    <WishlistBadge lookSlug={look.slug} onClick={onWishlistBadgeClick} />
                </LooksTileUiKit.TopRightBadge>
            )}
            <LooksTileUiKit.ImageWrapper
                as={HybridLink}
                to={ldpURL}
                isPortrait={isPortrait}
                onClick={() => handleClick(LooksTileTrackingIdentifier.LOOK)}
            >
                <LLPImage
                    alt={look.name}
                    src={look.portraitImage}
                    mobileSrc={look[isPortrait ? 'portraitImage' : 'landscapeImage']}
                />
            </LooksTileUiKit.ImageWrapper>
            <TextBox>
                <TextBox.Prefix
                    as={HybridLink}
                    to={roomURL}
                    testId="looks-tile-category"
                    onClick={() => handleClick(LooksTileTrackingIdentifier.CATEGORY)}
                >
                    {look.roomName || <>&nbsp;</>}
                </TextBox.Prefix>
                <Title
                    as={HybridLink}
                    to={ldpURL}
                    testId="looks-tile-title"
                    onClick={() => handleClick(LooksTileTrackingIdentifier.LOOK)}
                >
                    {look.name || <>&nbsp;</>}
                </Title>
                <TextBox.Cta
                    as={HybridLink}
                    to={ldpURL}
                    testId="looks-tile-cta"
                    onClick={() => handleClick(LooksTileTrackingIdentifier.LOOK)}
                >
                    {t('Shop the Look')}
                </TextBox.Cta>
            </TextBox>
        </StyledLooksTileUiKit>
    );
};

export default LooksTile;
